import { supabase } from "@/integrations/supabase/client";
import { discoverStorageBuckets, getBucketFileStructure } from "./bucketDiscovery";
import { extractStoragePath, normalizeToPath } from "./storageUtils";

interface OrphanedFile {
  bucket: string;
  path: string;
  size: number;
  created_at: string;
}

interface DatabaseFileReference {
  column: string;
  table: string;
  value: string;
}

const normalizePath = (path: string): string => {
  // Remove leading slash if present
  return path.startsWith('/') ? path.substring(1) : path;
};

const extractFileNameFromPath = (path: string): string | null => {
  if (!path) return null;
  
  // Handle storage paths - extract just the filename
  const normalized = normalizeToPath(path);
  return normalized.split('/').pop() || null;
};

const checkEpisodeThumbnails = async (): Promise<OrphanedFile[]> => {
  try {
    const bucketStructure = await getBucketFileStructure('episode-thumbnails');
    
    if (bucketStructure.structure === 'error') {
      console.warn('Episode thumbnails bucket is not accessible, skipping...');
      return [];
    }
    
    const allFiles = [...bucketStructure.rootFiles, ...bucketStructure.subfolderFiles];
    
    if (allFiles.length === 0) {
      console.log('No files found in episode-thumbnails bucket');
      return [];
    }

    // Get all thumbnail URLs from episodes table
    const { data: episodes, error: episodesError } = await supabase
      .from('episodes')
      .select('thumbnail_url');
    
    if (episodesError) {
      console.error('Error fetching episodes:', episodesError);
      return [];
    }
    
    console.log(`Found ${episodes?.length || 0} episodes with thumbnails`);
    
    const usedFiles = new Set<string>();
    episodes?.forEach(episode => {
      if (episode.thumbnail_url) {
        const fileName = extractFileNameFromPath(episode.thumbnail_url);
        if (fileName) {
          usedFiles.add(normalizePath(fileName));
        }
      }
    });
    
    console.log('Used thumbnail files:', Array.from(usedFiles));
    
    const orphanedFiles = allFiles
      .filter(file => !usedFiles.has(normalizePath(file.name)))
      .map(file => ({
        bucket: 'episode-thumbnails',
        path: bucketStructure.structure === 'subfolder' ? `episode-thumbnails/${file.name}` : file.name,
        size: file.metadata?.size || 0,
        created_at: file.created_at || '',
      }));
    
    console.log(`Found ${orphanedFiles.length} orphaned episode thumbnails`);
    return orphanedFiles;
  } catch (error) {
    console.error('Error checking episode thumbnails:', error);
    return [];
  }
};

const checkCharacterImages = async (): Promise<OrphanedFile[]> => {
  try {
    const bucketStructure = await getBucketFileStructure('character-images');
    
    if (bucketStructure.structure === 'error') {
      console.warn('Character images bucket is not accessible, skipping...');
      return [];
    }
    
    const allFiles = [...bucketStructure.rootFiles, ...bucketStructure.subfolderFiles];
    
    if (allFiles.length === 0) {
      console.log('No files found in character-images bucket');
      return [];
    }

    const { data: characters, error: charactersError } = await supabase
      .from('characters')
      .select('image_url, avatar_url');
    
    if (charactersError) {
      console.error('Error fetching characters:', charactersError);
      return [];
    }
    
    console.log(`Found ${characters?.length || 0} characters with images`);
    
    const usedFiles = new Set<string>();
    characters?.forEach(character => {
      [character.image_url, character.avatar_url].forEach(url => {
        if (url) {
          const fileName = extractFileNameFromPath(url);
          if (fileName) {
            usedFiles.add(normalizePath(fileName));
          }
        }
      });
    });
    
    console.log('Used character image files:', Array.from(usedFiles));
    
    const orphanedFiles = allFiles
      .filter(file => !usedFiles.has(normalizePath(file.name)))
      .map(file => ({
        bucket: 'character-images',
        path: bucketStructure.structure === 'subfolder' ? `character-images/${file.name}` : file.name,
        size: file.metadata?.size || 0,
        created_at: file.created_at || '',
      }));
    
    console.log(`Found ${orphanedFiles.length} orphaned character images`);
    return orphanedFiles;
  } catch (error) {
    console.error('Error checking character images:', error);
    return [];
  }
};

const checkActivityFiles = async (): Promise<OrphanedFile[]> => {
  try {
    const bucketStructure = await getBucketFileStructure('activities');
    
    if (bucketStructure.structure === 'error') {
      console.warn('Activities bucket is not accessible, skipping...');
      return [];
    }
    
    const allFiles = [...bucketStructure.rootFiles, ...bucketStructure.subfolderFiles];
    
    console.log(`Activity bucket structure: ${bucketStructure.structure}`);
    console.log(`Found ${allFiles.length} total files in activities bucket`);
    
    if (allFiles.length === 0) {
      console.log('No files found in activities bucket');
      return [];
    }

    const { data: activities, error: activitiesError } = await supabase
      .from('activities')
      .select('download_url');
    
    if (activitiesError) {
      console.error('Error fetching activities:', activitiesError);
      return [];
    }
    
    console.log(`Found ${activities?.length || 0} activities with download URLs`);
    
    const usedFiles = new Set<string>();
    activities?.forEach(activity => {
      if (activity.download_url) {
        const fileName = extractFileNameFromPath(activity.download_url);
        if (fileName) {
          usedFiles.add(fileName);
          console.log(`Activity file reference: ${activity.download_url} -> normalized: ${fileName}`);
        }
      }
    });
    
    console.log('Used activity files:', Array.from(usedFiles));
    console.log('Available files:', allFiles.map(f => f.name));
    
    const orphanedFiles = allFiles
      .filter(file => !usedFiles.has(file.name))
      .map(file => ({
        bucket: 'activities',
        path: bucketStructure.structure === 'subfolder' ? `activities/${file.name}` : file.name,
        size: file.metadata?.size || 0,
        created_at: file.created_at || '',
      }));
    
    console.log(`Found ${orphanedFiles.length} orphaned activity files`);
    return orphanedFiles;
  } catch (error) {
    console.error('Error checking activity files:', error);
    return [];
  }
};

const checkPartnerLogos = async (): Promise<OrphanedFile[]> => {
  try {
    const bucketStructure = await getBucketFileStructure('partner-logos');
    
    if (bucketStructure.structure === 'error') {
      console.warn('Partner logos bucket is not accessible, skipping...');
      return [];
    }
    
    const allFiles = [...bucketStructure.rootFiles, ...bucketStructure.subfolderFiles];
    
    if (allFiles.length === 0) {
      console.log('No files found in partner-logos bucket');
      return [];
    }

    const { data: partners, error: partnersError } = await supabase
      .from('partners')
      .select('logo_url');
    
    if (partnersError) {
      console.error('Error fetching partners:', partnersError);
      return [];
    }
    
    console.log(`Found ${partners?.length || 0} partners with logos`);
    
    const usedFiles = new Set<string>();
    partners?.forEach(partner => {
      if (partner.logo_url) {
        const fileName = extractFileNameFromPath(partner.logo_url);
        if (fileName) {
          usedFiles.add(normalizePath(fileName));
          console.log(`Partner logo reference: ${partner.logo_url} -> normalized: ${fileName}`);
        }
      }
    });
    
    console.log('Used partner logo files:', Array.from(usedFiles));
    
    const orphanedFiles = allFiles
      .filter(file => !usedFiles.has(normalizePath(file.name)))
      .map(file => ({
        bucket: 'partner-logos',
        path: bucketStructure.structure === 'subfolder' ? `partner-logos/${file.name}` : file.name,
        size: file.metadata?.size || 0,
        created_at: file.created_at || '',
      }));
    
    console.log(`Found ${orphanedFiles.length} orphaned partner logos`);
    return orphanedFiles;
  } catch (error) {
    console.error('Error checking partner logos:', error);
    return [];
  }
};

export const findAllOrphanedFiles = async (): Promise<OrphanedFile[]> => {
  console.log('Starting comprehensive orphan file scan...');
  
  const orphanedFiles: OrphanedFile[] = [];
  
  try {
    // Discover accessible buckets
    const buckets = await discoverStorageBuckets();
    console.log('Accessible buckets:', buckets.map(b => b.name));
    
    // Check each known bucket type
    const bucketCheckers = [
      { name: 'episode-thumbnails', checker: checkEpisodeThumbnails },
      { name: 'character-images', checker: checkCharacterImages },
      { name: 'activities', checker: checkActivityFiles },
      { name: 'partner-logos', checker: checkPartnerLogos },
    ];
    
    for (const { name, checker } of bucketCheckers) {
      if (buckets.some(bucket => bucket.name === name)) {
        console.log(`Checking ${name} bucket...`);
        const files = await checker();
        orphanedFiles.push(...files);
      } else {
        console.log(`Bucket ${name} not accessible, skipping...`);
      }
    }
    
  } catch (error) {
    console.error('Error in comprehensive orphan file scan:', error);
  }
  
  console.log(`Total orphaned files found: ${orphanedFiles.length}`);
  return orphanedFiles;
};

export const deleteOrphanedFiles = async (orphanedFiles: OrphanedFile[]): Promise<void> => {
  for (const file of orphanedFiles) {
    try {
      const { error } = await supabase.storage
        .from(file.bucket)
        .remove([file.path]);
      
      if (error) {
        console.error(`Failed to delete ${file.path} from ${file.bucket}:`, error);
      } else {
        console.log(`Deleted orphaned file: ${file.path} from ${file.bucket}`);
      }
    } catch (error) {
      console.error(`Error deleting ${file.path}:`, error);
    }
  }
};

export const getOrphanedFilesStats = (orphanedFiles: OrphanedFile[]) => {
  const totalSize = orphanedFiles.reduce((sum, file) => sum + file.size, 0);
  const totalCount = orphanedFiles.length;
  
  const byBucket = orphanedFiles.reduce((acc, file) => {
    if (!acc[file.bucket]) {
      acc[file.bucket] = { count: 0, size: 0 };
    }
    acc[file.bucket].count++;
    acc[file.bucket].size += file.size;
    return acc;
  }, {} as Record<string, { count: number; size: number }>);
  
  return {
    totalCount,
    totalSize,
    totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2),
    byBucket,
  };
};
